import { environment } from 'src/environments/environment';

export class StorageKeys {
    public static TOKEN: string = "TOKEN";
    public static LANGUAGE: string = "LANGUAGE";
}

export class ConfigStrings {
    public static API_URL: string = environment.apiUrl;
}

export class ErrorCodes {
    public static INVALID_PATH: string = "INVALID_PATH";
    public static MISSING_REQUIRED_DATA = "MISSING_REQUIRED_DATA";
    public static INVALID_SERVICE: string = "INVALID_SERVICE";
    public static INVALID_FOLDER_NAME: string = "INVALID_FOLDER_NAME";
    public static DUPLICATE_FILE_NAME: string = "DUPLICATE_FILE_NAME";
    public static DUPLICATE_FOLDER_NAME: string = "DUPLICATE_FOLDER_NAME";
    public static PARENT_FOLDER_NOT_FOUND: string = "PARENT_FOLDER_NOT_FOUND";
    public static DESTINATION_IS_SUBFOLDER: string = "DESTINATION_IS_SUBFOLDER";
    public static USER_NOT_FOUND: string = "USER_NOT_FOUND";
    public static FILE_NOT_FOUND: string = "FILE_NOT_FOUND";
    public static FOLDER_NOT_FOUND: string = "FOLDER_NOT_FOUND";
    public static TREE_NOT_FOUND: string = "TREE_NOT_FOUND";
    public static SESSION_NOT_FOUND: string = "SESSION_NOT_FOUND";
    public static DUPLICATE_TREE_NAME: string = "DUPLICATE_TREE_NAME";
    public static UNKNOWN_ERROR: string = "UNKNOWN_ERROR";
    public static CONNECTION_REFUSED: string = "CONNECTION_REFUSED";
    public static LOAD_PREVIEW_ERROR: string = "LOAD_PREVIEW_ERROR";
    public static UPLOAD_ABORTED: string = "UPLOAD_ABORTED";
}

export class Error {
    public error: any;

    constructor(error: any) {
        this.error = error;
    }
}

export class Language {
    public languageId: string;
    public name: string;
}

export class User {
    public userId: string;
    public tenantId: string;
    public active: boolean;
    public forename: string;
    public surname: string;
    public username: string;
    public email: string;
    public cid: string;
    public nickname: string;
    public tempUserCode: string;
    public activateUserCode: string;
    public recoveryPassCode: string;
    public modifyDate: Date;
    public modifyUserid: string;
    public mailDisabled: string;
    public phoneNumber: string;
    public langCode: string; // 2 caratteri minuscoli ("it", "en", ecc)
    public timezone: string; // Nel formato "Europe/Rome"
    public avatarImage: string;
    public password: string;
    public loginDate: Date;
}

export class UserSession {
    public userSessionId: string;
    public userId: string;
    public token: string;
    public active: boolean;
    public creationDate: Date;
    public lastActionDate: Date;
}

export class MediaTree {
    public mediaTreeId: string;
    public name: string;
    public description: string;
}

export class MediaTreeAuthorization {
    public mediaTreeAuthorizationId: string;
    public tenantId: string;
    public mediaTreeId: string;
    public creationDate: Date;
}

export class MediaFolder {
    public mediaFolderId: string;
    public name: string;
    public description: string;
    public creationData: Date;
    public modifyDate: Date;
    public mediaTreeId: string;
    public parentFolderId: string;
    public translations: string;
}

export class MediaFile {
    public mediaFileId: string;
    public name: string;
    public description: string;
    public attributes: string;
    public mimeType: string;
    public url: string;
    public previewUrl: string;
    public storageType: string;
    public mediaFolderId: string;
    public translations: string;
}

export class MediaTranslation {
    name: string;
    description: string;
    mediaTranslationId: string;
    languageId: string;
    mediaFileId: string;
    mediaFolderId: string;
}

export class FolderDialogData {
    public node: FolderNode;
    public mediaTree: MediaTree;
}

export class SelectFolderData {
    public node: FolderNode;
    public tree: MediaTree;
}

export class FileDialogData {
    public files: Array<FileNode>;
    public node: FolderNode;
    public mediaTree: MediaTree;
}

export class FolderMoveResultData {
    public folder: MediaFolder;
    public parentFolder: FolderNode;

    constructor(folder: MediaFolder, parentFolder: FolderNode) {
        this.folder = folder;
        this.parentFolder = parentFolder;
    }
}

export class RenameFolderDialogData {
    public node: FolderNode;
}

export class RenameFileDialogData {
    public file: FileNode;
}

export class UploadFileDialogData {
    public folder: FolderNode;
}

export class ConfirmDialogData {
    public title: string;
    public message: string;
}

export function sortFolderByName(left: FolderNode, right: FolderNode) {
    let a: string = left.name.toLowerCase();
    let b: string = right.name.toLowerCase();
    if (a === null && b === null) { return 0; }
    if (a === null) { return 1; }
    if (b === null) { return -1; }

    return a.localeCompare(b);
}

export function buildMap(obj: object): Map<string, any> {
    let map = new Map();
    Object.keys(obj).forEach(key => {
        map.set(key, obj[key]);
    });
    return map;
}

export class FolderNode {
    public mediaFolderId: string;
    public name: string;
    public description: string;
    public parentFolderId: string;
    public mediaTreeId: string;
    public level: number = 1;
    public expandable: boolean = true;
    public isLoading: boolean = false;
    public translations: Map<string, MediaTranslation>;

    static fromMediaFolder(mediaFolder: MediaFolder, level: number): FolderNode {
        let folderNode: FolderNode = new FolderNode();
        folderNode.mediaFolderId = mediaFolder.mediaFolderId;
        folderNode.name = mediaFolder.name;
        folderNode.description = mediaFolder.description;
        folderNode.mediaTreeId = mediaFolder.mediaTreeId;
        folderNode.parentFolderId = mediaFolder.parentFolderId;
        folderNode.level = level;
        folderNode.translations = buildMap(JSON.parse(mediaFolder.translations));
        return folderNode;
    }

    getName(languageId: string): string {
        if (this.translations && this.translations.get(languageId) && this.translations.get(languageId).name) {
            return this.translations.get(languageId).name;
        }

        return this.name;
    }

    getDescription(languageId: string): string {
        if (this.translations && this.translations.get(languageId) && this.translations.get(languageId).description) {
            return this.translations.get(languageId).description;
        }

        return this.description;
    }
}

export class FileNode {
    public mediaFileId: string;
    public name: string;
    public description: string;
    public attributes: string;
    public mimeType: string;
    public url: string;
    public previewUrl: string;
    public storageType: string;
    public mediaFolderId: string;
    public checked: boolean;
    public disabled: boolean;
    public translations: Map<string, MediaTranslation>;

    static fromMediaFile(mediaFile: MediaFile): FileNode {
        let fileNode: FileNode = new FileNode;
        fileNode.mediaFileId = mediaFile.mediaFileId;
        fileNode.name = mediaFile.name;
        fileNode.description = mediaFile.description;
        fileNode.attributes = mediaFile.attributes;
        fileNode.mimeType = mediaFile.mimeType;
        fileNode.url = mediaFile.url;
        fileNode.previewUrl = mediaFile.previewUrl;
        fileNode.storageType = mediaFile.storageType;
        fileNode.mediaFolderId = mediaFile.mediaFolderId;
        fileNode.checked = false;
        fileNode.disabled = false;
        fileNode.translations = buildMap(JSON.parse(mediaFile.translations));
        return fileNode;
    }

    getName(languageId: string): string {
        if (this.translations && this.translations.get(languageId) && this.translations.get(languageId).name) {
            return this.translations.get(languageId).name;
        }

        return this.name;
    }

    getDescription(languageId: string): string {
        if (this.translations && this.translations.get(languageId) && this.translations.get(languageId).description) {
            return this.translations.get(languageId).description;
        }

        return this.description;
    }

}
